import type { OrderDetailDTO } from "@/modules/orders/domain/contracts";
import { formatDateLocal, formatTimeLocal } from "@/shared/lib/date-time";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import { toast } from "@devhop/ui";
import { useMemo } from "react";
import { useAddOrderAttachment, useDeleteOrderAttachment } from "../api/queries";
import {
  nextAttachmentSortOrder,
  OrderAttachmentGrid,
} from "./OrderAttachmentGrid";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງແພັກ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ສິນຄ້າຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ / ຮັບຄືນຄັງ",
  cancelled: "ຍົກເລີກ",
};

export function OrderStatusHistoryTimeline({
  order,
}: {
  order: OrderDetailDTO;
}) {
  const addAttachment = useAddOrderAttachment(order.id);
  const deleteAttachment = useDeleteOrderAttachment(order.id);

  const packingItems = useMemo(
    () => order.attachments.filter((a) => a.kind === "packing"),
    [order.attachments],
  );
  const shippingItems = useMemo(
    () => order.attachments.filter((a) => a.kind === "shipping_document"),
    [order.attachments],
  );

  const canUploadPacking = order.status === "packing";
  const canDeletePacking = order.status === "packing";
  const canUploadShipping =
    order.status === "shipped" || order.status === "completed";
  const canDeleteShipping = canUploadShipping;

  const runUpload = async (
    kind: "packing" | "shipping_document",
    keys: string[],
    blurhashes: string[],
    allowed: boolean,
  ) => {
    if (!allowed) return;
    let sortBase = nextAttachmentSortOrder(
      kind === "packing" ? packingItems : shippingItems,
    );
    for (let i = 0; i < keys.length; i++) {
      const fileKey = keys[i];
      if (!fileKey) continue;
      const bh = blurhashes[i];
      await addAttachment.mutateAsync({
        kind,
        fileKey,
        sortOrder: sortBase++,
        metadata: bh ? { blurhash: bh } : undefined,
      });
    }
    if (keys.length) toast.success("ບັນທຶກຮູບສຳເລັດ");
  };

  if (!order.statusHistory.length) {
    return <p className="text-muted-foreground text-sm">ບໍ່ມີປະຫວັດ</p>;
  }

  const reversed = [...order.statusHistory].reverse();
  const hasShippedEvent = reversed.some((r) => r.status === "shipped");
  let packingBlockPlaced = false;
  let shippingBlockPlaced = false;

  return (
    <div className="space-y-0">
      {reversed.map((row, i) => {
        let placePacking = false;
        if (row.status === "packing" && !packingBlockPlaced) {
          packingBlockPlaced = true;
          placePacking = true;
        }

        let placeShipping = false;
        if (
          !shippingBlockPlaced &&
          (row.status === "shipped" ||
            (row.status === "completed" && !hasShippedEvent))
        ) {
          shippingBlockPlaced = true;
          placeShipping = true;
        }

        const showPackingPanel =
          placePacking &&
          (packingItems.length > 0 || canUploadPacking);
        const showShippingPanel =
          placeShipping &&
          (shippingItems.length > 0 || canUploadShipping);

        return (
          <div key={row.id} className="relative flex gap-4 pb-6">
            {i < reversed.length - 1 && (
              <div className="absolute top-6 left-[7px] h-full w-px bg-border" />
            )}
            <div
              className={`relative z-0 size-4 shrink-0 rounded-full ${
                i === 0 ? "bg-foreground" : "bg-muted"
              }`}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                ປ່ຽນສະຖານະເປັນ{" "}
                <span className="font-medium">
                  {STATUS_LABELS[row.status] ?? row.status}
                </span>
              </p>
              <p className="text-muted-foreground text-xs">
                {formatDateLocal(row.createdAt)} •{" "}
                {formatTimeLocal(row.createdAt)} ໂດຍລະບົບ
              </p>
              {row.note && (
                <p className="mt-1 text-muted-foreground text-sm">{row.note}</p>
              )}

              {showPackingPanel && (
                <div className="mt-3 rounded-lg border border-dashed border-primary/25 bg-muted/20 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-foreground text-xs uppercase tracking-wide">
                      ຮູບການແພັກ
                    </p>
                    {canUploadPacking && (
                      <PresignedImageUploadTrigger
                        keyPrefix={`uploads/orders/${order.id}/packing`}
                        label="ອັບໂຫຼດ"
                        multiple
                        disabled={addAttachment.isPending}
                        isSaving={addAttachment.isPending}
                        className="[&_button]:h-8 [&_button]:px-2 [&_button]:py-1 [&_button]:text-xs"
                        onSuccess={(key, blurhash) =>
                          void runUpload(
                            "packing",
                            [key],
                            [blurhash ?? ""],
                            canUploadPacking,
                          )
                        }
                        onSuccessMultiple={(keys, blurhashes) =>
                          void runUpload(
                            "packing",
                            keys,
                            blurhashes,
                            canUploadPacking,
                          )
                        }
                      />
                    )}
                  </div>
                  <OrderAttachmentGrid
                    items={packingItems}
                    canDelete={canDeletePacking}
                    onDelete={(id) => deleteAttachment.mutate(id)}
                    isDeletingId={
                      deleteAttachment.isPending &&
                      deleteAttachment.variables != null
                        ? deleteAttachment.variables
                        : null
                    }
                    compact
                  />
                </div>
              )}

              {showShippingPanel && (
                <div className="mt-3 rounded-lg border border-dashed border-primary/25 bg-muted/20 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-foreground text-xs uppercase tracking-wide">
                      ຮູບບິນຂົນສົ່ງ
                    </p>
                    {canUploadShipping && (
                      <PresignedImageUploadTrigger
                        keyPrefix={`uploads/orders/${order.id}/shipping`}
                        label="ອັບໂຫຼດ"
                        multiple
                        disabled={addAttachment.isPending}
                        isSaving={addAttachment.isPending}
                        className="[&_button]:h-8 [&_button]:px-2 [&_button]:py-1 [&_button]:text-xs"
                        onSuccess={(key, blurhash) =>
                          void runUpload(
                            "shipping_document",
                            [key],
                            [blurhash ?? ""],
                            canUploadShipping,
                          )
                        }
                        onSuccessMultiple={(keys, blurhashes) =>
                          void runUpload(
                            "shipping_document",
                            keys,
                            blurhashes,
                            canUploadShipping,
                          )
                        }
                      />
                    )}
                  </div>
                  <OrderAttachmentGrid
                    items={shippingItems}
                    canDelete={canDeleteShipping}
                    onDelete={(id) => deleteAttachment.mutate(id)}
                    isDeletingId={
                      deleteAttachment.isPending &&
                      deleteAttachment.variables != null
                        ? deleteAttachment.variables
                        : null
                    }
                    compact
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
